import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "semantic-ui-react";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { GridPager } from "../../common/pagination/GridPager";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import TableContainer from "../../common/ui/table-container/TableContainer";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "../../common/ui/data-table/DataTable.css";
import "rc-pagination/assets/index.css";
import "./Attendance.css";
import { AttendanceGuideButton, AttendanceHelpModal } from "./AttendanceHelp";
import {
  fetchAttendanceReportingExport,
  fetchAttendanceReportingRangePaged,
  fetchAttendanceReportingStats,
  fetchAttendanceReportingTodayPaged,
  handleAttendanceApiError,
  triggerBlobDownload,
} from "./attendanceApi";
import { formatAttendanceTime } from "./attendanceDateUtils";
import { AttendanceReportingPeriodMode, AttendanceWorkplaceType } from "./attendanceConstants";
import {
  addDays,
  formatMonthRangeLabel,
  formatWeekRangeReadable,
  getWeekStartMonday,
  startOfMonth,
  toUtcDateOnlyISO,
} from "../timesheets/timesheetDateUtils";
import { WebStorage, WebStorageNames } from "../../../utils";

const DEFAULT_PAGE_SIZE = 20;

/** Match API `StatusFilter` / row `Status` strings (server normalizes case and dashes). */
const REPORTING_STATUS_FILTER_OPTIONS = [
  { value: "Absent", labelKey: "app.attendance.reports.statusOptionAbsent" },
  { value: "Present — On time", labelKey: "app.attendance.reports.statusOptionPresentOnTime" },
  { value: "Present — Late", labelKey: "app.attendance.reports.statusOptionPresentLate" },
  { value: "Present — Open session", labelKey: "app.attendance.reports.statusOptionPresentOpenSession" },
  { value: "Irregular — No check-in", labelKey: "app.attendance.reports.statusOptionIrregularNoCheckIn" },
];

/** @param {number | null | undefined} n */
const reportingRowWorkplaceTypeLabel = (n, t) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v === AttendanceWorkplaceType.Remote) return t("app.attendance.reports.workplaceRemote");
  if (v === AttendanceWorkplaceType.FieldVisit) return t("app.attendance.reports.workplaceField");
  return t("app.attendance.reports.workplaceOffice");
};

/** @param {string} name */
const initialsFromName = (name) => {
  const p = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!p.length) return "?";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return `${p[0][0]}${p[p.length - 1][0]}`.toUpperCase();
};

/** @param {{ url: string | null, name: string }} props */
const ReportingEmployeeAvatar = ({ url, name }) => {
  const [broken, setBroken] = useState(false);
  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        className="att-cr-queue-avatar__img"
        onError={() => setBroken(true)}
      />
    );
  }
  return <span className="att-cr-queue-avatar__fallback">{initialsFromName(name)}</span>;
};

/**
 * Map API status label to outline pill class (same family as correction queue tags).
 * @param {string | null | undefined} statusLabel
 */
const reportingStatusTagClass = (statusLabel) => {
  const s = String(statusLabel ?? "").trim().toLowerCase();
  if (!s) return "att-cr-queue-status-tag--unknown";
  if (/\b(absent|absence|missing|no\s*show|no-show)\b/.test(s) || s.includes("absent")) return "att-cr-queue-status-tag--rejected";
  if (s.includes("irregular")) return "att-cr-queue-status-tag--pending";
  if (/\b(late|delayed|delay)\b/.test(s) || s.includes("late")) return "att-cr-queue-status-tag--pending";
  if (/\b(half|partial|incomplete|flex)\b/.test(s)) return "att-cr-queue-status-tag--info";
  if (/\b(leave|vacation|holiday|pto|day\s*off)\b/.test(s)) return "att-cr-queue-status-tag--withdrawn";
  if (
    /\b(present|complete|full|attended|worked|normal|ok|good|clear)\b/.test(s) ||
    s.includes("on time") ||
    s.includes("on-time") ||
    s.includes("ontime")
  ) {
    return "att-cr-queue-status-tag--approved";
  }
  if (/\b(remote|office|field|wfh)\b/.test(s) || s.includes("work from")) return "att-reporting-status-tag--workplace";
  return "att-cr-queue-status-tag--unknown";
};

/** @param {{ code: number, ariaCheckedIn: string, ariaMissed: string, ariaNonWorking: string }} props */
const ReportingCalendarStatusIcon = ({ code, ariaCheckedIn, ariaMissed, ariaNonWorking }) => {
  const c = Number(code);
  if (c === 1) {
    return (
      <span className="att-reporting-cal-cell att-reporting-cal-cell--in" role="img" aria-label={ariaCheckedIn} title={ariaCheckedIn}>
        <i className="bi bi-check-lg att-reporting-cal-cell__glyph" aria-hidden />
      </span>
    );
  }
  if (c === 0) {
    return (
      <span className="att-reporting-cal-cell att-reporting-cal-cell--missed" role="img" aria-label={ariaMissed} title={ariaMissed}>
        <i className="bi bi-x-lg att-reporting-cal-cell__glyph" aria-hidden />
      </span>
    );
  }
  if (c === 2) {
    return (
      <span className="att-reporting-cal-cell att-reporting-cal-cell--off" role="img" aria-label={ariaNonWorking} title={ariaNonWorking}>
        <i className="bi bi-dash att-reporting-cal-cell__glyph att-reporting-cal-cell__glyph--dash" aria-hidden />
      </span>
    );
  }
  return <span className="text-muted">—</span>;
};

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  const n = parseInt(String(r), 10);
  return Number.isFinite(n) ? n : null;
};

const isCompanyAdmin = (rid) => rid === 1 || rid === 2;

const pctDisplay = (n) => (n != null && Number.isFinite(Number(n)) ? `${Number(n).toFixed(1)}%` : "—");

/** @param {unknown} n */
const formatReportingWorkHours = (n) => {
  if (n == null || n === "") return null;
  const x = Number(n);
  if (!Number.isFinite(x)) return null;
  return x.toFixed(2);
};

const clampPct = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.min(100, Math.max(0, x));
};

const AttendanceReportsPage = () => {
  const { t } = useTranslation();
  const roleId = getRoleId();
  const allowed = isCompanyAdmin(roleId);
  const [helpKey, setHelpKey] = useState(null);

  const [periodMode, setPeriodMode] = useState(AttendanceReportingPeriodMode.Today);
  const [dayAnchor, setDayAnchor] = useState(() => new Date());
  const [weekStart, setWeekStart] = useState(() => getWeekStartMonday(new Date()));
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));

  const [officeId, setOfficeId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [workplaceType, setWorkplaceType] = useState("");
  /** Today reporting only — API `StatusFilter`. */
  const [statusFilter, setStatusFilter] = useState("");
  /** Substring filter on employee name or email (API `Search`; not sent to Reporting/Stats). */
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");
  const [offices, setOffices] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [tablePage, setTablePage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reportingStats, setReportingStats] = useState(/** @type {Awaited<ReturnType<typeof fetchAttendanceReportingStats>> | null} */ (null));
  const [todayPaged, setTodayPaged] = useState(/** @type {Awaited<ReturnType<typeof fetchAttendanceReportingTodayPaged>> | null} */ (null));
  const [rangePaged, setRangePaged] = useState(/** @type {Awaited<ReturnType<typeof fetchAttendanceReportingRangePaged>> | null} */ (null));
  /** Which range rows show the per-day attendance matrix (Week/Month). */
  const [expandedRangeRowKeys, setExpandedRangeRowKeys] = useState(() => new Set());
  const [loadingKpi, setLoadingKpi] = useState(false);
  const [loadingTable, setLoadingTable] = useState(true);
  const [exportLoading, setExportLoading] = useState(/** @type {null | "excel" | "csv" | "pdf"} */ (null));

  const weekEndDate = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const weekLabel = formatWeekRangeReadable(weekStart, weekEndDate);

  const baseOpts = useMemo(() => {
    const wpt = workplaceType === "" ? undefined : Number(workplaceType);
    const oid = officeId === "" ? undefined : officeId;
    const did = departmentId === "" ? undefined : departmentId;
    const base = {
      periodMode,
      dayUtc: periodMode === AttendanceReportingPeriodMode.Today ? dayAnchor : undefined,
      weekRangeStart: periodMode === AttendanceReportingPeriodMode.Weekly ? weekStart : undefined,
      weekRangeEnd: periodMode === AttendanceReportingPeriodMode.Weekly ? weekEndDate : undefined,
      filterYear: periodMode === AttendanceReportingPeriodMode.Monthly ? monthAnchor.getFullYear() : undefined,
      filterMonth: periodMode === AttendanceReportingPeriodMode.Monthly ? monthAnchor.getMonth() + 1 : undefined,
      officeId: oid,
      departmentId: did,
      workplaceType: wpt !== undefined && !Number.isNaN(wpt) ? wpt : undefined,
      pageSize,
    };
    if (periodMode === AttendanceReportingPeriodMode.Today) {
      const sf = String(statusFilter ?? "").trim();
      if (sf) base.statusFilter = sf;
    }
    const sq = String(debouncedEmployeeSearch ?? "").trim();
    if (sq) base.search = sq;
    return base;
  }, [
    periodMode,
    dayAnchor,
    weekStart,
    weekEndDate,
    monthAnchor,
    officeId,
    departmentId,
    workplaceType,
    statusFilter,
    debouncedEmployeeSearch,
    pageSize,
  ]);

  /** Stats API ignores `Search` — strip so KPIs stay global for period/filters. */
  const statsOpts = useMemo(() => {
    const { search: _search, ...rest } = baseOpts;
    return rest;
  }, [baseOpts]);

  const queryWithTablePage = useMemo(() => ({ ...baseOpts, page: tablePage }), [baseOpts, tablePage]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedEmployeeSearch(employeeSearch.trim()), 350);
    return () => window.clearTimeout(id);
  }, [employeeSearch]);

  useLayoutEffect(() => {
    setTablePage(1);
  }, [periodMode, dayAnchor, weekStart, monthAnchor, officeId, departmentId, workplaceType, statusFilter, debouncedEmployeeSearch]);

  useLayoutEffect(() => {
    setExpandedRangeRowKeys(new Set());
  }, [
    tablePage,
    pageSize,
    periodMode,
    dayAnchor,
    weekStart,
    monthAnchor,
    officeId,
    departmentId,
    workplaceType,
    statusFilter,
    debouncedEmployeeSearch,
  ]);

  useEffect(() => {
    const load = async () => {
      try {
        const { Department, Office } = config.REST_API;
        const [depRes, offRes] = await Promise.all([axiosBasic.get(Department.Get), axiosBasic.get(Office.GetCompanyOffices)]);
        const depBody = depRes.data || {};
        if (depBody.success && Array.isArray(depBody.data)) {
          setDepartments(
            depBody.data
              .map((d) => ({ id: d.id, name: d.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
        const offBody = offRes.data || {};
        if (offBody.success && Array.isArray(offBody.data)) {
          setOffices(
            offBody.data
              .map((o) => ({ id: o.id, name: o.countryName ? `${o.name} (${o.countryName})` : o.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
      } catch {
        setDepartments([]);
        setOffices([]);
      }
    };
    load();
  }, []);

  /** KPI / stats — refetch when period or filters change, not when table page changes */
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      setLoadingKpi(true);
      try {
        const stats = await fetchAttendanceReportingStats(statsOpts);
        if (!cancelled) setReportingStats(stats);
      } catch (e) {
        if (!cancelled) {
          handleAttendanceApiError(e, t("app.attendance.errors.loadReport"));
          setReportingStats(null);
        }
      } finally {
        if (!cancelled) setLoadingKpi(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, statsOpts, t]);

  /** Table — Today; Week/Month: Reporting/Range (summary + Columns + Statuses per row) */
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      setLoadingTable(true);
      try {
        if (periodMode === AttendanceReportingPeriodMode.Today) {
          const today = await fetchAttendanceReportingTodayPaged(queryWithTablePage);
          if (!cancelled) {
            setTodayPaged(today);
            setRangePaged(null);
          }
        } else {
          const range = await fetchAttendanceReportingRangePaged(queryWithTablePage);
          if (!cancelled) {
            setRangePaged(range);
            setTodayPaged(null);
          }
        }
      } catch (e) {
        if (!cancelled) {
          handleAttendanceApiError(e, t("app.attendance.errors.loadReport"));
          setTodayPaged(null);
          setRangePaged(null);
        }
      } finally {
        if (!cancelled) setLoadingTable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, periodMode, queryWithTablePage, t]);

  const runExport = async (format) => {
    if (!allowed) return;
    setExportLoading(format === "xlsx" ? "excel" : format);
    try {
      /** Raw reporting opts — fetchAttendanceReportingExport runs buildAttendanceReportingQuery internally. */
      const blob = await fetchAttendanceReportingExport(format, {
        ...baseOpts,
        page: tablePage,
        pageSize,
      });
      const ext = format === "pdf" ? "pdf" : format === "csv" ? "csv" : "xlsx";
      const stamp =
        periodMode === AttendanceReportingPeriodMode.Today
          ? toUtcDateOnlyISO(dayAnchor).slice(0, 10)
          : periodMode === AttendanceReportingPeriodMode.Weekly
            ? `week-${toUtcDateOnlyISO(weekStart).slice(0, 10)}`
            : `${monthAnchor.getFullYear()}-${String(monthAnchor.getMonth() + 1).padStart(2, "0")}`;
      triggerBlobDownload(blob, `attendance-reporting-${stamp}.${ext}`);
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.reports.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const shiftDay = (delta) => {
    setDayAnchor((d) => addDays(d, delta));
  };

  const shiftMonthNav = (delta) => {
    setMonthAnchor((m) => {
      const x = new Date(m);
      x.setMonth(x.getMonth() + delta);
      return startOfMonth(x);
    });
  };

  const shiftWeekNav = (delta) => {
    setWeekStart((w) => addDays(w, delta * 7));
  };

  const tableRowsToday = todayPaged?.items ?? [];
  const tableRowsRange = rangePaged?.items ?? [];
  const rangeDayColumns = rangePaged?.columns ?? [];
  const totalTable =
    periodMode === AttendanceReportingPeriodMode.Today
      ? todayPaged?.totalCount ?? 0
      : rangePaged?.totalCount ?? 0;
  const showPagination = totalTable > 0;
  const paginationFrom = totalTable > 0 ? (tablePage - 1) * pageSize + 1 : 0;
  const paginationTo = totalTable > 0 ? Math.min(tablePage * pageSize, totalTable) : 0;

  const latePct = reportingStats?.latePercentage != null ? clampPct(reportingStats.latePercentage) : null;
  const onTimeShare = latePct != null ? clampPct(100 - latePct) : null;

  const toolbarPeriodHint =
    periodMode === AttendanceReportingPeriodMode.Today
      ? t("app.attendance.reports.toolbarHintToday")
      : periodMode === AttendanceReportingPeriodMode.Weekly
        ? t("app.attendance.reports.toolbarHintWeek")
        : t("app.attendance.reports.toolbarHintMonth");

  const isViewingToday = useMemo(() => {
    if (periodMode !== AttendanceReportingPeriodMode.Today) return true;
    const now = new Date();
    return (
      dayAnchor.getFullYear() === now.getFullYear() &&
      dayAnchor.getMonth() === now.getMonth() &&
      dayAnchor.getDate() === now.getDate()
    );
  }, [periodMode, dayAnchor]);

  const showJumpToToday = periodMode === AttendanceReportingPeriodMode.Today && !isViewingToday;

  const handleTablePagerChange = useCallback(
    (current, nextPageSize) => {
      const resolvedSize = nextPageSize != null && nextPageSize !== "" ? Number(nextPageSize) : pageSize;
      if (resolvedSize !== pageSize) {
        setPageSize(resolvedSize);
        setTablePage(1);
      } else {
        setTablePage(current);
      }
    },
    [pageSize]
  );

  const handleRowsPerPageChange = useCallback((n) => {
    setPageSize(n);
    setTablePage(1);
  }, []);

  const toggleRangeRowExpanded = useCallback((rowKey) => {
    setExpandedRangeRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  if (!allowed) {
    return (
      <div className="px-3 py-4">
        <p className="text-muted">{t("app.attendance.errors.loadReport")}</p>
      </div>
    );
  }

  return (
    <div className="documents-page att-cr-page att-cr-queue-page att-reporting-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-bar-chart-line" />}
        title={t("app.attendance.reports.title")}
        description={t("app.attendance.reports.subtitle")}
        rightContent={<AttendanceGuideButton onClick={() => setHelpKey("reports")} />}
      />

      <Grid stackable className="att-cr-grid">
        <Grid.Row>
          <Grid.Column width={16}>
            <section className="ts-appr-stats att-cr-queue-stats att-reporting-stats" aria-label={t("app.attendance.reports.statsAria")}>
              <div className="ts-appr-stat-card ts-appr-stat-card--total">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-people-fill" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">
                        {loadingKpi ? "…" : reportingStats?.totalPresent ?? "—"}
                      </span>
                      <span className="ts-appr-stat-card__label">{t("app.attendance.reports.kpiTotalPresent")}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ts-appr-stat-card ts-appr-stat-card--returned">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-alarm" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">
                        {loadingKpi ? "…" : pctDisplay(reportingStats?.latePercentage)}
                      </span>
                      <span className="ts-appr-stat-card__label">{t("app.attendance.reports.kpiLatePct")}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ts-appr-stat-card ts-appr-stat-card--approved">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-check-circle-fill" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">
                        {loadingKpi ? "…" : pctDisplay(reportingStats?.averageAttendanceRatePercent)}
                      </span>
                      <span className="ts-appr-stat-card__label">{t("app.attendance.reports.kpiAvgAttendanceRate")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <div className="att-reporting-charts-row" aria-label={t("app.attendance.reports.kpiSectionAria")}>
              <div className="att-reporting-chart-panel">
                <h3 className="att-reporting-chart-panel__title">{t("app.attendance.reports.chartLateShareTitle")}</h3>
                <p className="att-reporting-chart-panel__sub text-muted small mb-2">{t("app.attendance.reports.chartLateShareSubtitle")}</p>
                {!loadingKpi && latePct != null && onTimeShare != null ? (
                  <div className="att-reporting-chart-panel__content">
                    <div className="att-reporting-chart-legend att-reporting-chart-legend--primary">
                      <span className="att-reporting-chart-legend__item">
                        <i className="att-reporting-chart-legend__sw att-reporting-chart-legend__sw--late" aria-hidden />
                        {t("app.attendance.reports.chartLegendLate")} <strong>{latePct.toFixed(1)}%</strong>
                      </span>
                      <span className="att-reporting-chart-legend__item">
                        <i className="att-reporting-chart-legend__sw att-reporting-chart-legend__sw--on" aria-hidden />
                        {t("app.attendance.reports.chartLegendOnTime")} <strong>{onTimeShare.toFixed(1)}%</strong>
                      </span>
                    </div>
                    <div className="att-reporting-chart-panel__bar">
                      <div
                        className="att-reporting-stacked-track"
                        role="img"
                        aria-label={`${t("app.attendance.reports.chartLegendLate")} ${latePct.toFixed(1)}%, ${t("app.attendance.reports.chartLegendOnTime")} ${onTimeShare.toFixed(1)}%`}
                      >
                        <div className="att-reporting-stacked-seg att-reporting-stacked-seg--late" style={{ flex: Math.max(latePct, 0.001) }} />
                        <div className="att-reporting-stacked-seg att-reporting-stacked-seg--on" style={{ flex: Math.max(onTimeShare, 0.001) }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted small mb-0">{loadingKpi ? t("app.common.loading") : "—"}</p>
                )}
              </div>
              <div className="att-reporting-chart-panel">
                <h3 className="att-reporting-chart-panel__title">{t("app.attendance.reports.chartQualityTitle")}</h3>
                <p className="att-reporting-chart-panel__sub text-muted small mb-2">{t("app.attendance.reports.chartQualitySubtitle")}</p>
                {!loadingKpi && reportingStats?.averageAttendanceRatePercent != null ? (
                  <div className="att-reporting-chart-panel__content">
                    <div className="att-reporting-quality-value">{pctDisplay(reportingStats.averageAttendanceRatePercent)}</div>
                    <div className="att-reporting-chart-panel__bar">
                      <div className="att-reporting-meter" aria-hidden>
                        <div
                          className="att-reporting-meter__fill att-reporting-meter__fill--rate"
                          style={{ width: `${clampPct(reportingStats.averageAttendanceRatePercent)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted small mb-0">{loadingKpi ? t("app.common.loading") : "—"}</p>
                )}
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <section className="ts-time-log-shell att-cr-queue-filters att-reporting-filters" aria-label={t("app.attendance.reports.filtersAria")}>
              <div className="ts-appr-filters" role="search">
                <div className="ts-appr-filters__title text-muted" id="att-rep-filters-heading">
                  {t("app.attendance.reports.filtersToolbarLabel")}
                </div>

                <div className="ts-appr-filters__toolbar" aria-labelledby="att-rep-filters-heading">
                  <div className="ts-appr-filters__toolbar-left">
                    <div
                      className="ts-view-tabs ts-view-tabs--three ts-view-tabs--timelog-inline"
                      role="tablist"
                      aria-label={t("app.attendance.reports.periodModeAria")}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={periodMode === AttendanceReportingPeriodMode.Today}
                        className={periodMode === AttendanceReportingPeriodMode.Today ? "is-active" : ""}
                        onClick={() => setPeriodMode(AttendanceReportingPeriodMode.Today)}
                      >
                        {t("app.attendance.reports.periodToday")}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={periodMode === AttendanceReportingPeriodMode.Weekly}
                        className={periodMode === AttendanceReportingPeriodMode.Weekly ? "is-active" : ""}
                        onClick={() => {
                          setPeriodMode(AttendanceReportingPeriodMode.Weekly);
                          setWeekStart(getWeekStartMonday(dayAnchor));
                        }}
                      >
                        {t("app.attendance.reports.periodWeekly")}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={periodMode === AttendanceReportingPeriodMode.Monthly}
                        className={periodMode === AttendanceReportingPeriodMode.Monthly ? "is-active" : ""}
                        onClick={() => {
                          setPeriodMode(AttendanceReportingPeriodMode.Monthly);
                          setMonthAnchor(startOfMonth(dayAnchor));
                        }}
                      >
                        {t("app.attendance.reports.periodMonthly")}
                      </button>
                    </div>

                    <span className="ts-appr-filters__toolbar-divider d-none d-sm-block" aria-hidden />

                    {periodMode === AttendanceReportingPeriodMode.Today && (
                      <div className="ts-tl-period-toolbar-group ts-appr-filters__period-wrap">
                        <div className="ts-period-cluster ts-period-cluster--inline ts-period-cluster--compact" aria-label={t("app.attendance.reports.periodModeAria")}>
                          <button type="button" onClick={() => shiftDay(-1)} aria-label={t("app.attendance.reports.prevDay")}>
                            <i className="bi bi-chevron-left" aria-hidden />
                          </button>
                          <span className="ts-period-pill ts-period-pill--inline att-reporting-day-pill">
                            <i className="bi bi-calendar3 text-primary att-reporting-day-pill__icon" aria-hidden />
                            <input
                              type="date"
                              className="att-reporting-day-input border-0 bg-transparent"
                              value={`${dayAnchor.getFullYear()}-${String(dayAnchor.getMonth() + 1).padStart(2, "0")}-${String(dayAnchor.getDate()).padStart(2, "0")}`}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (!v) return;
                                const [y, m, d] = v.split("-").map(Number);
                                if (y && m && d) setDayAnchor(new Date(y, m - 1, d));
                              }}
                            />
                          </span>
                          <button type="button" onClick={() => shiftDay(1)} aria-label={t("app.attendance.reports.nextDay")}>
                            <i className="bi bi-chevron-right" aria-hidden />
                          </button>
                        </div>
                        {showJumpToToday ? (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm ts-tl-jump-current"
                            onClick={() => setDayAnchor(new Date())}
                            aria-label={t("app.attendance.reports.jumpToTodayAria")}
                          >
                            <i className="bi bi-calendar-check me-1" aria-hidden />
                            {t("app.attendance.reports.jumpToToday")}
                          </button>
                        ) : null}
                      </div>
                    )}

                    {periodMode === AttendanceReportingPeriodMode.Weekly && (
                      <div className="ts-tl-period-toolbar-group ts-appr-filters__period-wrap">
                        <div className="ts-period-cluster ts-period-cluster--inline ts-period-cluster--compact" aria-label={t("app.attendance.reports.periodModeAria")}>
                          <button type="button" onClick={() => shiftWeekNav(-1)} aria-label={t("app.timesheets.reports.periodPrevWeek")}>
                            <i className="bi bi-chevron-left" aria-hidden />
                          </button>
                          <span className="ts-period-pill ts-period-pill--inline">
                            <i className="bi bi-calendar-week text-primary" aria-hidden />
                            {weekLabel}
                          </span>
                          <button type="button" onClick={() => shiftWeekNav(1)} aria-label={t("app.timesheets.reports.periodNextWeek")}>
                            <i className="bi bi-chevron-right" aria-hidden />
                          </button>
                        </div>
                      </div>
                    )}

                    {periodMode === AttendanceReportingPeriodMode.Monthly && (
                      <div className="ts-tl-period-toolbar-group ts-appr-filters__period-wrap">
                        <div className="ts-period-cluster ts-period-cluster--inline ts-period-cluster--compact" aria-label={t("app.attendance.reports.periodModeAria")}>
                          <button type="button" onClick={() => shiftMonthNav(-1)} aria-label={t("app.timesheets.reports.periodPrevMonth")}>
                            <i className="bi bi-chevron-left" aria-hidden />
                          </button>
                          <span className="ts-period-pill ts-period-pill--inline">
                            <i className="bi bi-calendar3 text-primary" aria-hidden />
                            {formatMonthRangeLabel(monthAnchor)}
                          </span>
                          <button type="button" onClick={() => shiftMonthNav(1)} aria-label={t("app.timesheets.reports.periodNextMonth")}>
                            <i className="bi bi-chevron-right" aria-hidden />
                          </button>
                        </div>
                      </div>
                    )}

                    <span className="ts-appr-filters__toolbar-divider d-none d-md-block" aria-hidden />

                    <div
                      className="ts-time-log-export-group ts-time-log-export-group--labeled ts-appr-filters__exports"
                      role="group"
                      aria-label={t("app.timesheets.approvals.exportActionsAria")}
                    >
                      <button
                        type="button"
                        className="ts-tl-export-icon ts-tl-export-icon--excel ts-tl-export-icon--labeled"
                        onClick={() => runExport("xlsx")}
                        disabled={Boolean(exportLoading) || loadingTable}
                        title={t("app.attendance.reports.exportExcelTitle")}
                      >
                        {exportLoading === "excel" ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                        ) : (
                          <i className="bi bi-file-earmark-spreadsheet" aria-hidden />
                        )}
                        <span className="ts-tl-export-label">{t("app.timesheets.common.exportExcelShort")}</span>
                      </button>
                      <button
                        type="button"
                        className="ts-tl-export-icon ts-tl-export-icon--csv ts-tl-export-icon--labeled"
                        onClick={() => runExport("csv")}
                        disabled={Boolean(exportLoading) || loadingTable}
                        title={t("app.attendance.reports.exportCsvTitle")}
                      >
                        {exportLoading === "csv" ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                        ) : (
                          <i className="bi bi-filetype-csv" aria-hidden />
                        )}
                        <span className="ts-tl-export-label">{t("app.timesheets.common.exportCsvShort")}</span>
                      </button>
                      <button
                        type="button"
                        className="ts-tl-export-icon ts-tl-export-icon--pdf ts-tl-export-icon--labeled"
                        onClick={() => runExport("pdf")}
                        disabled={Boolean(exportLoading) || loadingTable}
                        title={t("app.attendance.reports.exportPdfTitle")}
                      >
                        {exportLoading === "pdf" ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                        ) : (
                          <i className="bi bi-file-earmark-pdf" aria-hidden />
                        )}
                        <span className="ts-tl-export-label">{t("app.timesheets.common.exportPdfShort")}</span>
                      </button>
                    </div>
                  </div>
                  <p className="ts-appr-filters__toolbar-hint ts-appr-controls__hint mb-0">{toolbarPeriodHint}</p>
                </div>

                <div
                  className={
                    "ts-appr-filters__fields att-reporting-filters__fields" +
                    (periodMode === AttendanceReportingPeriodMode.Today
                      ? " att-reporting-filters__fields--with-status"
                      : "")
                  }
                >
                  <div className="ts-filter-field ts-filter-field--search att-reporting-filters__search">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-rep-search">
                      {t("app.attendance.reports.filterSearch")}
                    </label>
                    <input
                      id="att-rep-search"
                      type="search"
                      autoComplete="off"
                      placeholder={t("app.attendance.reports.filterSearchPlaceholder")}
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                      aria-label={t("app.attendance.reports.filterSearchAria")}
                    />
                  </div>
                  <div className="ts-filter-field">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-rep-office">
                      {t("app.attendance.reports.office")}
                    </label>
                    <select id="att-rep-office" value={officeId} onChange={(e) => setOfficeId(e.target.value)}>
                      <option value="">{t("app.attendance.reports.all")}</option>
                      {offices.map((o) => (
                        <option key={o.id} value={String(o.id)}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ts-filter-field">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-rep-dept">
                      {t("app.attendance.reports.department")}
                    </label>
                    <select id="att-rep-dept" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                      <option value="">{t("app.attendance.reports.all")}</option>
                      {departments.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ts-filter-field">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-rep-work-location">
                      {t("app.attendance.reports.workLocation")}
                    </label>
                    <select id="att-rep-work-location" value={workplaceType} onChange={(e) => setWorkplaceType(e.target.value)}>
                      <option value="">{t("app.attendance.reports.all")}</option>
                      <option value={String(AttendanceWorkplaceType.Office)}>{t("app.attendance.reports.workplaceOffice")}</option>
                      <option value={String(AttendanceWorkplaceType.Remote)}>{t("app.attendance.reports.workplaceRemote")}</option>
                      <option value={String(AttendanceWorkplaceType.FieldVisit)}>{t("app.attendance.reports.workplaceField")}</option>
                    </select>
                  </div>
                  {periodMode === AttendanceReportingPeriodMode.Today ? (
                    <div className="ts-filter-field">
                      <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-rep-status-filter">
                        {t("app.attendance.reports.statusFilter")}
                      </label>
                      <select
                        id="att-rep-status-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        aria-label={t("app.attendance.reports.statusFilter")}
                      >
                        <option value="">{t("app.attendance.reports.all")}</option>
                        {REPORTING_STATUS_FILTER_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {t(o.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                  <div className="ts-filter-field att-reporting-filters__rows-per-page">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-rep-page-size">
                      {t("app.attendance.reports.rowsPerPage")}
                    </label>
                    <select
                      id="att-rep-page-size"
                      value={pageSize}
                      onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                      aria-label={t("app.attendance.reports.rowsPerPage")}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </section>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <div className="att-table-card att-cr-queue-table-wrap ts-table-wrap ts-table-wrap--approvals">
              <TableContainer>
                {periodMode === AttendanceReportingPeriodMode.Today ? (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0 att-cr-queue-table">
                      <thead>
                        <tr>
                          <th scope="col">{t("app.attendance.reports.colEmployee")}</th>
                          <th scope="col">{t("app.attendance.reports.colOffice")}</th>
                          <th scope="col">{t("app.attendance.reports.colDept")}</th>
                          <th scope="col">{t("app.attendance.reports.colIn")}</th>
                          <th scope="col">{t("app.attendance.reports.colOut")}</th>
                          <th scope="col">{t("app.attendance.reports.colHours")}</th>
                          <th scope="col">{t("app.attendance.reports.colAttendanceWorkLocation")}</th>
                          <th scope="col" className="text-end">
                            {t("app.attendance.reports.colStatus")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingTable && tableRowsToday.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center py-5 text-muted">
                              <span className="spinner-border spinner-border-sm text-primary me-2" aria-hidden />
                              {t("app.common.loading")}
                            </td>
                          </tr>
                        ) : null}
                        {!loadingTable && tableRowsToday.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center py-5 text-muted">
                              {t("app.attendance.reports.emptyTable")}
                            </td>
                          </tr>
                        ) : null}
                        {tableRowsToday.map((r) => (
                          <tr key={`${r.userId}-${r.email}-${r.checkInUtc}`}>
                            <td className="att-cr-queue-cell-employee">
                              <div className="att-cr-queue-person att-cr-queue-person--head">
                                <div className="att-cr-queue-avatar">
                                  <ReportingEmployeeAvatar url={r.imageUrl} name={r.employeeName} />
                                </div>
                                <div className="att-cr-queue-person__text">
                                  <div className="att-cr-queue-person__name">{r.employeeName || "—"}</div>
                                  <div className="att-cr-queue-person__email text-muted small">{r.email || "—"}</div>
                                  {String(r.designation ?? "").trim() ? (
                                    <div className="att-cr-queue-person__meta small">{r.designation}</div>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                            <td>{r.officeName || "—"}</td>
                            <td>{r.departmentName || "—"}</td>
                            <td className="text-nowrap small">{r.checkInUtc ? formatAttendanceTime(r.checkInUtc) : "—"}</td>
                            <td className="text-nowrap small">{r.checkOutUtc ? formatAttendanceTime(r.checkOutUtc) : "—"}</td>
                            <td>{formatReportingWorkHours(r.workHours) ?? "—"}</td>
                            <td className="small">{reportingRowWorkplaceTypeLabel(r.workplaceType, t)}</td>
                            <td className="text-end att-reporting-status-cell">
                              {String(r.statusLabel ?? "").trim() ? (
                                <span
                                  className={`att-cr-queue-status-tag ${reportingStatusTagClass(r.statusLabel)}`}
                                  title={r.statusLabel}
                                >
                                  {r.statusLabel}
                                </span>
                              ) : (
                                <span className="att-cr-queue-status-tag att-cr-queue-status-tag--unknown">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="att-reporting-range-outer-wrap">
                    <table className="table table-hover align-middle mb-0 att-cr-queue-table att-reporting-range-table att-reporting-range-table--fixed">
                      <thead>
                        <tr>
                          <th scope="col" className="att-reporting-range-th-expand">
                            <span className="visually-hidden">{t("app.attendance.reports.expandAttendanceColumn")}</span>
                          </th>
                          <th scope="col">{t("app.attendance.reports.colEmployee")}</th>
                          <th scope="col">{t("app.attendance.reports.colOffice")}</th>
                          <th scope="col">{t("app.attendance.reports.colDept")}</th>
                          <th scope="col" className="text-end">
                            {t("app.attendance.reports.colRangeHours")}
                          </th>
                          <th scope="col" className="text-end">
                            {t("app.attendance.reports.colDaysPresent")}
                          </th>
                          <th scope="col" className="text-end">
                            {t("app.attendance.reports.colMissingIn")}
                          </th>
                          <th scope="col" className="text-end">
                            {t("app.attendance.reports.colMissingOut")}
                          </th>
                          <th scope="col" className="text-end att-reporting-range-th-missed-col">
                            {t("app.attendance.reports.colCalendarMissed")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingTable && tableRowsRange.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center py-5 text-muted">
                              <span className="spinner-border spinner-border-sm text-primary me-2" aria-hidden />
                              {t("app.common.loading")}
                            </td>
                          </tr>
                        ) : null}
                        {!loadingTable && tableRowsRange.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center py-5 text-muted">
                              {t("app.attendance.reports.emptyTable")}
                            </td>
                          </tr>
                        ) : null}
                        {tableRowsRange.map((r, idx) => {
                          const rowKey = String(r.userId || r.email || `row-${idx}`);
                          const expanded = expandedRangeRowKeys.has(rowKey);
                          const expandDomId = `att-rep-range-expand-${idx}`;
                          const detailDomId = `att-rep-range-detail-${idx}`;
                          return (
                            <Fragment key={rowKey}>
                              <tr>
                                <td className="att-reporting-range-td-expand align-middle">
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm att-reporting-range-expand-btn p-0"
                                    onClick={() => toggleRangeRowExpanded(rowKey)}
                                    aria-expanded={expanded}
                                    aria-controls={detailDomId}
                                    id={expandDomId}
                                    title={
                                      expanded
                                        ? t("app.attendance.reports.collapseAttendanceMatrix")
                                        : t("app.attendance.reports.expandAttendanceMatrix")
                                    }
                                  >
                                    <i className={`bi ${expanded ? "bi-chevron-down" : "bi-chevron-right"}`} aria-hidden />
                                  </button>
                                </td>
                                <td className="att-cr-queue-cell-employee">
                                  <div className="att-cr-queue-person att-cr-queue-person--head">
                                    <div className="att-cr-queue-avatar">
                                      <ReportingEmployeeAvatar url={r.imageUrl} name={r.employeeName} />
                                    </div>
                                    <div className="att-cr-queue-person__text">
                                      <div className="att-cr-queue-person__name">{r.employeeName || "—"}</div>
                                      <div className="att-cr-queue-person__email text-muted small">{r.email || "—"}</div>
                                      {String(r.designation ?? "").trim() ? (
                                        <div className="att-cr-queue-person__meta small">{r.designation}</div>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                <td>{r.officeName || "—"}</td>
                                <td>{r.departmentName || "—"}</td>
                                <td className="text-end">{formatReportingWorkHours(r.totalWorkHoursCompleted) ?? "—"}</td>
                                <td className="text-end">{r.daysWithAttendance}</td>
                                <td className="text-end">{r.missingCheckInCount}</td>
                                <td className="text-end">{r.missingCheckOutCount}</td>
                                <td
                                  className={`text-end text-nowrap small ${
                                    r.missedCheckedInCount > 0 ? "text-danger fw-semibold" : ""
                                  }`}
                                >
                                  {r.missedCheckedInCount}{" "}
                                  {t(
                                    r.missedCheckedInCount === 1
                                      ? "app.attendance.reports.calendarMissedDaySingular"
                                      : "app.attendance.reports.calendarMissedDayPlural"
                                  )}
                                </td>
                              </tr>
                              {expanded ? (
                                <tr className="att-reporting-range-detail-tr">
                                  <td colSpan={9} className="att-reporting-range-detail-cell p-0 border-0" id={detailDomId}>
                                    <div
                                      className="att-reporting-range-expand-panel"
                                      role="region"
                                      aria-labelledby={expandDomId}
                                    >
                                      {rangeDayColumns.length === 0 ? (
                                        <p className="text-muted small mb-0 px-3 py-2">{t("app.attendance.reports.noDayColumnsInRange")}</p>
                                      ) : (
                                        <div className="att-reporting-range-matrix-scroll">
                                          <table className="table table-sm align-middle mb-0 att-reporting-calendar-grid att-reporting-calendar-grid--embedded">
                                            <thead>
                                              <tr>
                                                {rangeDayColumns.map((col, cidx) => (
                                                  <th
                                                    key={`${col.label}-${cidx}`}
                                                    scope="col"
                                                    className="att-reporting-calendar-grid__th-day text-center"
                                                  >
                                                    <div className="att-reporting-calendar-grid__th-day-inner">
                                                      {col.weekdayShort ? (
                                                        <span className="att-reporting-calendar-grid__th-weekday">{col.weekdayShort}</span>
                                                      ) : null}
                                                      <span className="att-reporting-calendar-grid__th-date">{col.label}</span>
                                                    </div>
                                                  </th>
                                                ))}
                                                <th
                                                  scope="col"
                                                  className="att-reporting-calendar-grid__th-missed text-center att-reporting-calendar-grid__th-missed--label"
                                                >
                                                  {t("app.attendance.reports.colCalendarMissed")}
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              <tr>
                                                {rangeDayColumns.map((_, dayIdx) => (
                                                  <td key={`d-${dayIdx}`} className="att-reporting-calendar-grid__td-day text-center">
                                                    <ReportingCalendarStatusIcon
                                                      code={r.statuses[dayIdx] ?? 0}
                                                      ariaCheckedIn={t("app.attendance.reports.calendarAriaCheckedIn")}
                                                      ariaMissed={t("app.attendance.reports.calendarAriaMissed")}
                                                      ariaNonWorking={t("app.attendance.reports.calendarAriaNonWorking")}
                                                    />
                                                  </td>
                                                ))}
                                                <td
                                                  className={`att-reporting-calendar-grid__td-missed text-center text-nowrap small ${
                                                    r.missedCheckedInCount > 0 ? "text-danger fw-semibold" : ""
                                                  }`}
                                                >
                                                  {r.missedCheckedInCount}{" "}
                                                  {t(
                                                    r.missedCheckedInCount === 1
                                                      ? "app.attendance.reports.calendarMissedDaySingular"
                                                      : "app.attendance.reports.calendarMissedDayPlural"
                                                  )}
                                                </td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ) : null}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </TableContainer>
              {showPagination ? (
                <div
                  className={`documents-pagination documents-pagination--grid-pager${loadingTable ? " is-loading" : ""}`}
                  aria-busy={loadingTable}
                  aria-label={t("app.attendance.reports.paginationAria")}
                >
                  <span className="documents-pagination-total">
                    {t("app.attendance.correctionsQueue.paginationRange", {
                      from: paginationFrom,
                      to: paginationTo,
                      total: totalTable,
                    })}
                  </span>
                  <div className="documents-pagination-pager-wrap">
                    <GridPager
                      className="documents-rc-pagination"
                      onChange={handleTablePagerChange}
                      current={tablePage}
                      total={totalTable}
                      pageSize={pageSize}
                      defaultPageSize={DEFAULT_PAGE_SIZE}
                      showLessItems
                      showTitle={false}
                      showSizeChanger={false}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <AttendanceHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AttendanceReportsPage;
